import { useEffect, useRef, useState } from 'react';
import { Link } from 'next-translate-routes';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import MicroModal from 'micromodal';
import Image from 'next/image';
// Utils
import { cn } from 'utils';
// Components
import PropTypes from 'prop-types';
import Title from '../../atoms/Title/Title';
import Button from '../../atoms/Button/Button';
import CarouselBlock from '../../atoms/CarouselBlock/CarouselBlock';
import { Picto } from '../../atoms/Picto/Picto';
// Constants
import { MEDIUM_LONG_STAY, SHORT_STAY } from '../../../src/constants';
// Styles
import styles from './MobileAppartments.module.css';
import RichTextContent from '../../atoms/RichTextContent/RichTextContent';

const Tooltip = dynamic(() => import('../../atoms/Tooltip/Tooltip'), { ssr: false });
const Modal = dynamic(() => import('../../organisms/Modal/Modal'), { ssr: false });

const VIRTUAL_VISIT_MODAL = 'virtualVisitModal';

function MobileAppartment({
  theme,
  label,
  size,
  minPrice,
  images,
  isLongStay,
  isMediumStay,
  activity,
  taxesInformationsLongStay,
  taxesInformationsMediumStay,
  equipment,
  virtualVisit,
  longDescription,
  isFirstElement,
}) {
  // Hooks
  const { t } = useTranslation();

  // States and const
  const detailsRef = useRef(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(!!isFirstElement);
  const [displayVirtualVisit, setDisplayVirtualVisit] = useState('');

  // Effects
  useEffect(() => {
    if (detailsRef.current) {
      if (isDetailsOpen) {
        detailsRef.current.style.height = `${detailsRef.current.scrollHeight}px`;
      } else {
        detailsRef.current.style.height = '0px';
      }
    }
  }, [isDetailsOpen]);

  // Handlers
  const handleDetailsClick = () => {
    setIsDetailsOpen(!isDetailsOpen);
  };

  const handleVirtualVisitModal = (id) => {
    setDisplayVirtualVisit(id);
    MicroModal.show(id);
  };

  return (
    <article className={styles.card}>
      <CarouselBlock medias={images} size="compact" />

      <div className={styles.cardContent}>
        <div className={styles.titleWrapper}>
          <h3 className={styles.contentTitle}>
            {label}
          </h3>
          {size ? (
            <div className={styles.area}>
              {t('apartmentBlock.surface', { surface: size })}
            </div>
          ) : null}
        </div>
        <div className={styles.priceContainer}>
          {minPrice > 0 && activity === MEDIUM_LONG_STAY ? (
            <>
              <p className={styles.price}>
                {t('apartmentBlock.priceMobile', { price: minPrice })}
              </p>
              <span className={styles.priceContent}>
                {t('apartmentBlock.perMonthMobile')}
                {isLongStay || isMediumStay ? (
                  <Tooltip
                    arrow={false}
                    visible
                    className={styles.tooltip}
                    content={(
                      <>
                        {isLongStay
                          ? (<div>{taxesInformationsLongStay}</div>)
                          : null}
                        {!isLongStay && isMediumStay
                          ? (<div>{taxesInformationsMediumStay}</div>)
                          : null}
                      </>
                    )}
                  >
                    <div>
                      <Picto icon="information" className={styles.apartmentPicto} />
                    </div>
                  </Tooltip>
                ) : null}
              </span>
            </>
          ) : null}

          {minPrice > 0 && activity === SHORT_STAY ? (
            <span className={styles.price}>{t('apartmentBlock.priceNightMobile', { price: minPrice })}</span>
          ) : null}

        </div>

        <div ref={detailsRef} className={cn([styles.details, isDetailsOpen && styles.detailsOpen])}>
          {equipment?.length ? (
            <ul className={styles.equipments}>
              {equipment.map(({ data: item }, index) => (
                <li key={index} className={styles.equipmentsItem}>
                  {item?.media?.url ? (
                    <div className={styles.equipmentsItemImage}>
                      <Image
                        src={item.media.url}
                        alt={item?.media?.description}
                        sizes="24px"
                        width={24}
                        height={24}
                      />
                    </div>
                  ) : null}
                  <RichTextContent content={item?.content} />
                </li>
              ))}
            </ul>
          ) : null}
          <RichTextContent
            className={styles.description}
            content={longDescription}
          />
          {virtualVisit ? (
            <Button
              icon="turn360"
              iconPosition="right"
              className={styles.desktop}
              CTAType="secondary"
              usageContext={theme}
              onClick={() => handleVirtualVisitModal(`${VIRTUAL_VISIT_MODAL}-${label}`)}
            >
              {t('apartmentBlock.virtualVisit')}
            </Button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleDetailsClick}
          className={styles.summary}
        >
          {t(isDetailsOpen ? 'apartmentBlock.displayLess' : 'apartmentBlock.displayMore')}
          <Picto icon="chevronDown" className={cn([styles.picto, isDetailsOpen && styles.pictoOpen])} />
        </button>
      </div>
      {virtualVisit ? (
        <Modal
          id={`${VIRTUAL_VISIT_MODAL}-${label}`}
          theme="virtualVisit"
          fullScreen
          hideCancelButton
        >
          {virtualVisit && displayVirtualVisit === `${VIRTUAL_VISIT_MODAL}-${label}` ? (
            <iframe
              title={`${label}-${virtualVisit}`}
              src={virtualVisit}
              width="100%"
              height="100%"
            />
          ) : null}
        </Modal>
      ) : null}
    </article>
  );
}

MobileAppartment.propTypes = {
  theme: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  size: PropTypes.number,
  minPrice: PropTypes.number,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLongStay: PropTypes.bool,
  isMediumStay: PropTypes.bool,
  activity: PropTypes.string.isRequired,
  taxesInformationsLongStay: PropTypes.string,
  taxesInformationsMediumStay: PropTypes.string,
  equipment: PropTypes.arrayOf(PropTypes.shape({})),
  virtualVisit: PropTypes.string,
  longDescription: PropTypes.string,
  isFirstElement: PropTypes.bool,
};

MobileAppartment.defaultProps = {
  size: 0,
  minPrice: 0,
  isLongStay: false,
  isMediumStay: false,
  taxesInformationsLongStay: '',
  taxesInformationsMediumStay: '',
  equipment: [],
  virtualVisit: '',
  longDescription: '',
  isFirstElement: false,
};

function MobileAppartments({
  title,
  subtitle,
  theme,
  apartments,
  ctaLabel,
  ctaLink,
  activity,
  isLongStay,
  isMediumStay,
  className,
}) {
  return (
    <div id={subtitle} className={cn([styles.wrapper, styles[theme], className])}>
      <Title
        title={title}
        subtitle={subtitle}
        theme={theme}
        className={styles.title}
      />
      {ctaLabel && ctaLink ? (
        <Link href={ctaLink} name={ctaLabel}>
          <Button
            CTAType="secondary"
            theme={theme}
            usageContext={theme}
            outline
            className={styles.commercialBtn}
          >
            {ctaLabel}
          </Button>
        </Link>
      ) : null}
      <div className={styles.appartments}>
        {apartments.map(({ data }, index) => (
          <MobileAppartment
            theme={theme}
            key={data.code}
            images={data.medias}
            minPrice={data.minPrice}
            taxesInformationsLongStay={data.taxesInformationsLongStay}
            taxesInformationsMediumStay={data.taxesInformationsMediumStay}
            label={data.label}
            size={data.size}
            equipment={data.equipment}
            virtualVisit={data.virtualVisit}
            longDescription={data.longDescription}
            isLongStay={isLongStay}
            isMediumStay={isMediumStay}
            activity={activity}
            isFirstElement={index === 0}
          />
        ))}
      </div>
    </div>
  );
}

MobileAppartments.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  apartments: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      code: PropTypes.string.isRequired,
      medias: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      minPrice: PropTypes.number,
      taxesInformationsLongStay: PropTypes.string,
      taxesInformationsMediumStay: PropTypes.string,
      label: PropTypes.string.isRequired,
      size: PropTypes.number,
      equipment: PropTypes.arrayOf(PropTypes.shape({})),
      virtualVisit: PropTypes.string,
      longDescription: PropTypes.string,
    }).isRequired,
  })).isRequired,
  ctaLabel: PropTypes.string,
  ctaLink: PropTypes.string,
  activity: PropTypes.string.isRequired,
  isLongStay: PropTypes.bool,
  isMediumStay: PropTypes.bool,
  className: PropTypes.string,
};

MobileAppartments.defaultProps = {
  ctaLabel: '',
  ctaLink: '',
  isLongStay: false,
  isMediumStay: false,
  className: '',
};

export default MobileAppartments;
